// @ts-nocheck
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { api } from '../services/api';

interface Genre {
    id?: string;
    code: string;
    name: string;
    featured?: boolean;
}

interface GenresContextType {
    genres: Genre[];
    isLoadingGenres: boolean;
    error: string | null;
    refetchGenres: () => Promise<void>;
}

const GenresContext = createContext<GenresContextType | undefined>(undefined);

export const useGenres = () => {
    const context = useContext(GenresContext);
    if (!context) {
        throw new Error('useGenres must be used within a GenresProvider');
    }
    return context;
};

interface GenresProviderProps {
    children: ReactNode;
}

export const GenresProvider: React.FC<GenresProviderProps> = ({ children }) => {
    const [genres, setGenres] = useState<Genre[]>([]);
    const [isLoadingGenres, setIsLoadingGenres] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchGenres = async () => {
        try {
            setIsLoadingGenres(true);
            setError(null);

            console.log('[PAIR-APP] Pre-loading genres...');
            const result = await api.getAllGenres();

            if (result.success && result.data && result.data.length > 0) {
                const formattedGenres = result.data.map((genre: any) => ({
                    id: genre.Genre?.id || genre.id,
                    code: genre.Genre?.code || genre.code || '',
                    name: genre.Genre?.name || genre.name || '',
                    featured: false,
                }));

                setGenres(formattedGenres);
                console.log(
                    '[PAIR-APP] Pre-loaded',
                    formattedGenres.length,
                    'genres'
                );
            } else {
                console.warn('[PAIR-APP] No genres returned from API');
                setGenres([]);
            }
        } catch (error: any) {
            console.error('[PAIR-APP] Failed to pre-load genres:', error);
            setError('Failed to load genres');
            setGenres([]);

            // Don't retry on auth errors
            if (error?.statusCode === 401 || error?.status === 401) {
                console.log(
                    '[PAIR-APP] Auth error while loading genres, will retry when user authenticates'
                );
            }
        } finally {
            setIsLoadingGenres(false);
        }
    };

    // Load genres on mount
    useEffect(() => {
        fetchGenres();
    }, []);

    const refetchGenres = async () => {
        await fetchGenres();
    };

    return (
        <GenresContext.Provider
            value={{ genres, isLoadingGenres, error, refetchGenres }}
        >
            {children}
        </GenresContext.Provider>
    );
};
